import { ChangeDetectorRef, Component, EventEmitter, Input, OnInit, Output, ViewChild } from '@angular/core';
import { ActivatedRoute } from '@angular/router';
import { TranslateModule } from '@ngx-translate/core';
import { AccessProviderDirective } from 'app/modules/shared/directives/access-provider.directive';
import { PermissionAccessService } from 'app/modules/shared/services/permission-access.service';
import * as _ from 'lodash';
import { ConfirmationService, MenuItem, MessageService } from 'primeng/api';
import { BreadcrumbModule } from 'primeng/breadcrumb';
import { TabViewModule } from 'primeng/tabview';
import { forkJoin } from 'rxjs';
import { AppIcons } from '../../../../shared/app.icons';
import { AppSettings } from '../../../../shared/app.settings';
import { MITableComponent } from '../../../../shared/components/mi-table/mi-table.component';
import { ModuleHeaderComponent } from '../../../../shared/components/module-header/module-header.component';
import type { Column } from '../../../../shared/models/attribute.models';
import type { Country } from '../../../../shared/models/country';
import type { EntityList } from '../../../../shared/models/entity.models';
import type { Language } from '../../../../shared/models/language';
import type { Values, Pagination } from '../../../../shared/models/listResponse.models';
import { CommonBindingDataService } from '../../../../shared/services/common-binding-data.service';
import { ConfigService } from '../../../../shared/services/config.service';
import { ButtonModule } from 'primeng/button';
import { DialogModule } from 'primeng/dialog';
import { AddSponsorUserComponent } from './add-user/add-user.component';
import { UserManagementService } from 'app/modules/user-management/services/user-management.service';

interface ExtendedColumn extends Column {
  attributeCode?: string;
  attributeIndex?: number;
  attributeId?: number;
  presetValues?: Array<{ labelKey: string; labelValue: string }>;
}

interface UserTableRow {
  id: string;
  userId: string;
  firstName: string;
  lastName: string;
  email: string;
  contactNumber: string;
  userRole: string;
  username: string;
  active: Record<string, string>;
  fullName: string;
  [key: string]: unknown;
}

interface UserResponse {
  data: Array<{
    userId: string;
    firstName: string;
    lastName: string;
    email: string;
    contactNumber: string;
    userRole: string;
    username: string;
    active: boolean;
    [key: string]: unknown;
  }>;
  pagination: Pagination;
  count: number;
}

interface FilterField {
  attributeCode: string;
  attributeValue: string;
  isDisable: boolean;
  presetValues: Array<{ labelValue: string; labelKey: string }>;
}

interface BulkUpdateEvent {
  data: Array<{ attributeValue: string }>;
  entityIds: string[];
}

interface BulkDeleteEvent {
  userIds: string[];
}

interface UserActionData {
  forTenantCode: string;
  active: string;
  entityCode: string;
}

interface UserSearchData extends EntityList {
  userType: string[];
  typeId: string[];
  roleName?: string;
}

@Component({
  selector: 'app-user-details',
  templateUrl: './user-details.component.html',
  styleUrl: './user-details.component.scss',
  standalone: true,
  imports: [TabViewModule, TranslateModule, MITableComponent, BreadcrumbModule, ButtonModule, DialogModule, AddSponsorUserComponent, AccessProviderDirective, ModuleHeaderComponent]
})
export class UserDetailsComponent implements OnInit {
  @ViewChild(MITableComponent) tableRef: MITableComponent;
  @Output() saveSuccess = new EventEmitter<void>();
  activeIndex: number = 0;
  isLoading = true;
  filterAvailableColumns: ExtendedColumn[] = [];
  cols: ExtendedColumn[] = [];
  @Input() moduleName: string;
  value: number = 50;
  btnLabel: string;
  items: MenuItem[] | undefined;
  isShowMenu: boolean = true;
  attributeLabels: Record<string, string> = {};
  attributeLabelsData: Record<string, unknown> = {};
  filterAttributeLabels: Record<string, string>;
  labelData = {
    attributeLabels: [
      { labelKey: 'manage_users_grid.fields.Status.label', labelValue: 'Status' },
      { labelKey: 'manage_users_grid.fields.UserRole.label', labelValue: 'User Role' }
    ]
  };
  listColumns: Values;
  allColumns: any;
  pagination: Pagination;
  totalRecords: number = 0;
  tableData: UserTableRow[] = [];
  buttons: Array<Record<string, unknown>> = [];
  country: Country;
  language: Language;
  entityData: UserSearchData = {
    actionStatus: "",
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: "",
    defaultSortColumn: "updatedAt",
    defaultSortType: "desc",
    status: 'both',
    userType: [AppSettings.ENTITY_CODE.SPONSOR],
    typeId: []
  };
  routePath: MenuItem[] = [];
  home: MenuItem = {};
  isModal: boolean = false;
  selectedColumns!: Column[];
  attributeData: Record<string, unknown>;
  data: Record<string, unknown>;
  rowItems: MenuItem[] | undefined;
  bulkSelectionsItems: MenuItem[] | undefined;
  moduleHeaderSecondaryBtnMenuItems: MenuItem[] = [];
  columnsData: ExtendedColumn[];
  bulkDeleteLabel: string = this.cs.getLabel('manage_users.delete');
  bulkUpdateLabel: string = this.cs.getLabel('manage_users.lbl_update_user');
  bulkUpdateHeader: string;
  reviewHeader: string;
  entityNameToDisplay: string;
  btnUpdate: string;
  deleteRoleHeaderMsg: string;
  deleteRoleMsg: string;
  showBulkUpdateDialog: boolean = false;
  visible: boolean = false;
  filterFieldsArray: FilterField[];
  uniqueId: string = 'sponsor_user_grid';
  isEntity: boolean = false;
  entityName: string;
  currentRow: UserTableRow;
  filterList: ExtendedColumn[];
  entityDataForRole: EntityList = {
    limit: AppSettings.PAGINATION_ROWS_PER_PAGE_LIMIT,
    offset: 0,
    searchStr: "",
    filters: [],
    countryCode: '',
    deleted: AppSettings.DELETED_TYPE.ONLY_NON_DELETED,
    forTenantCode: '',
    actionStatus: '',
    status: 'both',
  };
  miIcons = AppIcons;
  showAddDialog: boolean = false;
  userId: string;
  showImportExportBtn: boolean = false;
  sponsorId: string;
  userStatus: boolean;

  constructor(
    private cs: CommonBindingDataService,
    private messageService: MessageService,
    private confirmationService: ConfirmationService,
    private configService: ConfigService,
    private cd: ChangeDetectorRef,
    private permissionAccessService: PermissionAccessService,
    private userManagementService: UserManagementService,
    private route: ActivatedRoute
  ) { }

  ngOnInit() {
    this.setUpRoutePath();
    this.setUpRowItems();
    this.setupBulkSelectionsItems();
    this.initializeOtherVariablesAndAPI();
    this.initializeColumn();
    this.route.queryParams.subscribe(params => {
      this.sponsorId = params.id;
    })
  }

  initializeColumn() {
    this.allColumns = [
      {
        "attributeCode": "ID",
        "attributeIndex": 0,
        "field": "userCode",
        "header": this.cs.getLabel('manage_users.columns.lbl_id'),
        "isSortable": true
      },
      {
        "attributeCode": "email",
        "attributeIndex": 1,
        "field": "email",
        "header": this.cs.getLabel('manage_users.columns.lbl_user_email')
      },
      {
        "attributeCode": "first_name",
        "attributeIndex": 0,
        "isSortable": true,
        "field": "firstName",
        "header": this.cs.getLabel('manage_users.columns.lbl_first_name')
      },
      {
        "attributeCode": "last_name",
        "attributeIndex": 0,
        "field": "lastName",
        "header": this.cs.getLabel('manage_users.columns.lbl_last_name'),
        "isSortable": true
      },
      {
        "attributeCode": "contactNumber",
        "attributeIndex": 4,
        "field": "contactNumber",
        "header": this.cs.getLabel('manage_users.columns.lbl_phone_number'),
        "isSortable": true
      },
      {
        "attributeCode": "user_status",
        "attributeIndex": 5,
        "field": "active",
        "header": this.cs.getLabel('manage_users.columns.lbl_user_status'),
        "isSortable": true
      },
    ];
  }

  initializeOtherVariablesAndAPI() {
    this.entityData.forTenantCode = this.configService.getForTenantCode();
    this.btnLabel = this.cs.getLabel('manage_users.lbl_invite_user');
    this.bulkDeleteLabel = this.cs.getLabel('manage_users.message.delete_user');
    this.bulkUpdateLabel = this.cs.getLabel('manage_users.lbl_update_user');
    this.country = JSON.parse(localStorage.getItem(AppSettings.COUNTRY));
    this.language = JSON.parse(localStorage.getItem(AppSettings.LANGUAGE));
    this.bulkUpdateHeader = this.cs.getLabel('manage_users.lbl_bulk_update_header');
    this.entityNameToDisplay = this.cs.getLabel('manage_users.lbl_entity_name_display');
    this.btnUpdate = this.cs.getLabel('manage_users.btn_bulk_update');
    this.reviewHeader = this.cs.getLabel('manage_users.lbl_review_users');
    this.deleteRoleMsg = this.cs.getLabel('manage_users.message.confirm_delete_msg');
    this.deleteRoleHeaderMsg = this.cs.getLabel('manage_users.message.delete_user');
    this.getBulkUpdateAttributeData();
    this.getData();
  }

  private setUpRoutePath() {
    this.routePath = [
      { label: this.cs.getLabel('manage_users.lbl_route_path') },
    ];
  }

  private setUpRowItems() {
    const isEdit = this.permissionAccessService.hasAccess('USERS_AND_ROLES', '', 'ALL', 'EDIT');
    const isUpdateStatus = this.permissionAccessService.hasAccess('USERS_AND_ROLES', '', 'ALL', 'ACTIVATE');
    this.rowItems = [
      ...(isEdit ? [{
        label: this.cs.getLabel('manage_users.edit'),
        icon: `${AppIcons.EDIT_2} wh-122`,
        iconClass: 'bulk-update-icon',
        command: () => {
          this.update();
        }
      }] : []),
      ...(isUpdateStatus ? [{
        label: this.cs.getLabel('sponsor.lbl_activate'),
        icon: `${AppIcons.CIRCLE_CHECKED} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Active',
        command: () => {
          this.makeUserActiveInactive(this.currentRow, true);
        }
      }] : []),
      ...(isUpdateStatus ? [{
        label: this.cs.getLabel('sponsor.lbl_deactivate'),
        icon: `${AppIcons.ACTIVATE} wh-16`,
        iconClass: 'bulk-update-icon',
        value: 'Inactive',
        command: () => {
          this.makeUserActiveInactive(this.currentRow, false);
        }
      }] : []),
    ];
  }

  private setupBulkSelectionsItems() {
    const isBulkAction = this.permissionAccessService.hasAccess('USER_MANAGEMENT', 'MANAGE_USERS', 'DETAILS', 'BULK_ACTION');
    this.bulkSelectionsItems = [
      ...(isBulkAction ? [{
        label: this.bulkUpdateLabel,
        icon: `${AppIcons.EDIT_2} wh-122`,
        iconClass: 'bulk-update-icon',
        styleClass: 'bulk-update-list',
        command: (e: { item: { label: string } }) => {
          if (e.item.label === this.bulkUpdateLabel) {
            this.openBulkUpdateDialog();
          }
        },
      }] : []),
      ...(isBulkAction ? [{
        label: this.bulkDeleteLabel,
        icon: `${AppIcons.BASIC_TRAY} wh-122`,
        iconClass: 'bulk-update-icon',
        styleClass: 'bulk-update-list',
        command: () => {
          this.tableRef.delete(AppSettings.DELETE_TYPE.BULK);
        },
      }] : []),
    ];
  }

  private openBulkUpdateDialog() {
    this.visible = true;
    this.showBulkUpdateDialog = false;
    setTimeout(() => {
      this.showBulkUpdateDialog = true;
      this.tableRef.showBulkRowDialog();
    }, 200);
  }

  getFilterView() {
    this.filterAttributeLabels = this.cs.getAttributeLabels(this.labelData) as unknown as Record<string, string>;
    this.filterList = [
      {
        "attributeId": 6,
        "attributeCode": "Status",
        "attributeIndex": 5,
        "field": "status",
        "header": "Status",
        "presetValues": [
          {
            "labelKey": "Active",
            "labelValue": "Active",
          },
          {
            "labelKey": "Inactive",
            "labelValue": "Inactive",
          }
        ],
      }
    ];
  }

  getBulkUpdateAttributeData() {
    this.attributeData = {
      tabs: [
        {
          groups: [
            {
              fields: [
                {
                  attributeCode: 'user_status',
                  attributeValue: 'Status',
                  isDisable: false,
                  presetValues: [
                    { labelValue: 'Active', labelKey: 'Active' },
                    { labelValue: 'Inactive', labelKey: 'Inactive' },
                  ]
                }
              ]
            }
          ]
        }
      ]
    };
    this.updateFilterFieldsArray(this.attributeData.tabs[0]?.groups[0]?.fields as FilterField[]);
  }

  private updateFilterFieldsArray(fields: FilterField[]) {
    if (!fields) return;

    this.filterFieldsArray = [];
    fields.forEach(field => {
      if (field.attributeCode === 'user_status') {
        this.filterFieldsArray.push(field)
      }
    });
  }

  getTableView() {
    this.isLoading = false;
    this.searchEntity();

    this.filterAvailableColumns = _.clone(this.allColumns);
  }

  getData() {
    return forkJoin({
      tableViewData: this.getTableView(),
      filterViewDate: this.getFilterView(),
    })
  }

  searchEntity() {
    this.tableData = [];
    this.entityData.countryCode = this.country[0].countryCode;
    if(!this.entityData.typeId.length) {
      if (this.sponsorId) {
        this.entityData.typeId.push(this.sponsorId);
      } else {
        this.route.queryParams.subscribe(params => {
          this.sponsorId = params?.id;
          this.entityData.typeId.push(this.sponsorId);
        });
      }
    }
    this.userManagementService.searchEntityUser(this.entityData).subscribe((res: UserResponse) => {
      this.listColumns = {
        ...res?.data[0],
        additionalProp1: null,
        additionalProp2: null,
        additionalProp3: null,
        region_geofence: null
      };
      this.pagination = res?.pagination;
      this.totalRecords = res?.count;
      this.tableData = res?.data.map(data => {
        const fullName = `${data.firstName} ${data.lastName}`;
        const status = data.active ? { Active: 'Active' } : { Inactive: 'Inactive' };
        return {
          id: data.userId,
          ...data,
          fullName,
          active: status,
        };
      }) || [];
    });
  }

  onSearchValueChanges(event: string) {
    this.entityData.searchStr = event;
    this.searchEntity();
  }

  onFilterValueChange(event: Record<string, unknown>) {
    if (event.Status && (event.Status as string[]).length === 2 && (event.Status as string[]).includes('Active') && (event.Status as string[]).includes('Inactive')) {
      this.entityData.status = 'both';
    } else {
      this.entityData.status = (event.Status as string)?.toString() ? (event.Status as string).toString() : 'both';
    }

    if (event?.UserRole !== undefined && (event.UserRole as string[])?.length > 0) {
      this.entityData.roleName = (event.UserRole as string[]).join(',');
    } else {
      delete this.entityData.roleName;
    }

    this.entityData.offset = event.first as number;
    this.searchEntity();
  }

  onPageChange(event: { first: number; rows: number }) {
    this.entityData.offset = event?.first;
    this.entityData.limit = event?.rows;
    this.searchEntity();
  }

  itemPerPageChange(event: number) {
    this.entityData.limit = event;
    this.searchEntity();
  }

  update() {
    this.onUpdateUser(this.currentRow.userId);
  }

  onUpdateUser(userId: string) {
    this.userId = userId;
    this.showAddDialog = true;
  }

  makeUserActiveInactive(currentRow: UserTableRow, isActive: boolean) {
    this.userStatus = isActive;
    const headerMsg = !isActive ? this.cs.getLabel('manage_users.inactive_user_header_message') : this.cs.getLabel('manage_users.active_user_header_message');
    const bodyMsg = !isActive ? this.cs.getLabel('manage_users.inactive_status_message') : this.cs.getLabel('manage_users.active_status_message')
    const acceptLabel = !isActive ? this.cs.getLabel('manage_users.lbl_deactivate') : this.cs.getLabel('manage_users.lbl_activate');
    this.confirmationService.confirm({
      header: headerMsg,
      message: bodyMsg,
      acceptLabel,
      acceptIcon: 'none',
      rejectIcon: 'none',
      rejectLabel: this.cs.getLabel('manage_users.cancel'),
      rejectButtonStyleClass: 'bg-white text-color',
      acceptButtonStyleClass: !isActive ? 'bg-red-500' : 'bg-green-500',
      accept: () => {
        this.tableRef.changeStatusOfEntity();
      },
      reject: () => {
      }
    });
  }

  onChangeStatus(currentRow: UserTableRow) {
    const requestBody: UserActionData = {
      forTenantCode: this.configService.getForTenantCode(),
      active: this.userStatus ? 'true' : 'false',
      entityCode: AppSettings.ENTITY_CODE.SITE_USER
    };
    this.userManagementService.updateUserActionStatus(currentRow.userId, requestBody).subscribe(() => {
      setTimeout(() => {
        this.searchEntity();
      }, 500);
      if (this.userStatus) {
        this.showSuccessMessage('manage_users.message.msg_user_active');
      } else {
        this.showSuccessMessage('manage_users.message.msg_user_inactive');
      }
    }, error => {
      const errorMsg = error?.errors?.general?.[0]?.message ?? "An unknown error occurred.";
      this.showErrorMessage(errorMsg);
    });
  }

  onStaticBulkDataUpdate(event: BulkUpdateEvent) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      active: event.data[0].attributeValue === AppSettings.USERS_STATUS.ACTIVE,
      userIds: event.entityIds,
      entityCode: AppSettings.ENTITY_CODE.SITE_USER
    };
    this.userManagementService.updateBulkUserActionStatus(requestBody).subscribe({
      next: (res: { message: string }) => {
        this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: res.message });
        this.searchEntity();
      },
      error: (error: { errors: { general: Array<{ messageCode: string }> } }) => {
        this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].messageCode) });
      }
    })
  }

  onBulkDataDeleteEvent(event: BulkDeleteEvent) {
    const requestBody = {
      forTenantCode: this.configService.getForTenantCode(),
      userIds: event.userIds,
      entityCode: AppSettings.ENTITY_CODE.SITE_USER
    };
    this.userManagementService.deleteBulkUserActionStatus(requestBody).subscribe({
      next: (res: { message: string }) => {
        this.messageService.add({ key: 'tst', severity: 'success', summary: 'Success', detail: res.message });
        this.searchEntity();
      },
      error: (error: { errors: { general: Array<{ messageCode: string }> } }) => {
        this.messageService.add({ key: 'tst', severity: 'error', summary: 'Error', detail: this.cs.getLabel(error.errors.general[0].messageCode) });
      }
    })
  }

  onAddUser() {
    this.showAddDialog = true;
  }

  saveSuccessEvent() {
    this.saveSuccess.emit();
  }

  hidePopup(event: boolean) {
    if (event) {
      this.searchEntity();
    }
    this.userId = null;
    this.showAddDialog = false;
  }

  showSuccessMessage(labelKey: string) {
    this.messageService.add({
      key: 'tst',
      severity: 'success',
      summary: 'Successful',
      detail: this.cs.getLabel(labelKey),
    });
  }

  showErrorMessage(labelKey: string) {
    this.messageService.add({
      key: 'tst',
      severity: 'error',
      summary: 'Error',
      detail: this.cs.getLabel(labelKey)
    });
  }

  setCurrentRowData(event: UserTableRow) {
    this.currentRow = event;
    const status = Object.entries(event?.active)[0][0];
    this.setEditOptionDisabledForDeletedRecord(status);
  }

  setEditOptionDisabledForDeletedRecord(data: string) {
    this.rowItems.forEach(rowItem => {
      rowItem.disabled = rowItem.value === data;
    })
  }

  onSortData(event) {
    this.entityData.defaultSortColumn = event.field;
    this.entityData.defaultSortType = (event.sortOrder === 1) ? AppSettings.ASC_SORT_ORDER : AppSettings.DESC_SORT_ORDER;
    this.searchEntity();
  }
}
